import { ampli } from '@/ampli';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useMedia } from 'react-use';

import { shortFormatter } from '@/lib/formatting';

import { TextSkeleton } from '@/components/Skeleton';

import { CHAIN_IDS, NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import type { ZoraToken } from '@/hooks/useZora';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { Avatar } from '@/ui/Avatars/Avatar';
import { ArrowExternalIcon } from '@/ui/Icons/ArrowExternalIcon';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';
import { ChevronSelectorIcon } from '@/ui/Icons/ChevronSelectorIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';

import { classNames } from '@/utils/classnames';

import { makeImageProxyUrl } from '../../../utils/search';

interface ZoraTokenNode {
  id?: string;
  name?: string;
  symbol?: string;
  address?: string;
  chainId?: number;
  marketCap?: string;
  volume24h?: string;
  createdAt?: string;
  uniqueHolders?: number;
  mediaContent?: {
    previewImage?: {
      small?: string;
      medium?: string;
    };
  };
}

const columnHelper = createColumnHelper<ZoraTokenNode>();

/** Get the current sort direction for a column */
const getSortDirection = (sorting: SortingState, columnId: string) => {
  const sortEntry = sorting.find((s) => s.id === columnId);
  if (!sortEntry) {
    return undefined;
  }
  return sortEntry.desc ? 'desc' : 'asc';
};

/** Create a sortable header component */
const SortableHeader = ({
  title,
  columnId,
  sorting,
  onSort,
}: {
  title: string;
  columnId: string;
  sorting: SortingState;
  onSort: (columnId: string, direction: 'asc' | 'desc') => void;
}) => {
  const currentDirection = getSortDirection(sorting, columnId);
  const newDirection = currentDirection === 'desc' ? 'asc' : 'desc';

  return (
    <span
      className={classNames('flex items-center group gap-0.5 cursor-pointer')}
      onClick={() => onSort(columnId, newDirection)}
      role="button"
    >
      {title}
      <ChevronSelectorIcon
        className="text-token-gray-700 group-hover:text-token-gray-1000"
        upClass={
          currentDirection === 'asc' ? 'text-token-gray-1000' : 'group-hover:text-token-gray-700'
        }
        downClass={
          currentDirection === 'desc' ? 'text-token-gray-1000' : 'group-hover:text-token-gray-700'
        }
      />
    </span>
  );
};

/** Format time ago in compact format (e.g., "1h", "2d", "3mo") */
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString + 'Z');
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo`;
  }

  const years = Math.floor(months / 12);
  return `${years}y`;
};

const createColumns = (
  sorting: SortingState,
  onSort: (columnId: string, direction: 'asc' | 'desc') => void,
  formatFiat: (
    amount: number | string,
    showExtraSmallAmounts?: boolean,
    showDecimals?: boolean,
  ) => string,
) => [
  columnHelper.accessor('name', {
    header: 'Token',
    enableSorting: false,
    cell: ({ row }) => {
      const token = row.original;
      const imageUrl =
        token.chainId && token.address ? makeImageProxyUrl(token.chainId, token.address) : '';

      return (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar src={imageUrl} alt={token.name} size={32}>
            <div className="size-8 rounded-full bg-token-gray-200 flex-shrink-0" />
          </Avatar>
          <div className="flex flex-col min-w-0 max-w-[200px]">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-1600 truncate font-semibold',
              })}
            >
              {token.name || '-'}
            </span>
            <span
              className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-800 truncate' })}
            >
              {token.symbol || '-'}
            </span>
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('marketCap', {
    header: () => (
      <SortableHeader title="Market Cap" columnId="marketCap" sorting={sorting} onSort={onSort} />
    ),
    cell: ({ getValue }) => formatCurrencyValue(getValue(), formatFiat),
  }),
  columnHelper.accessor('volume24h', {
    header: () => (
      <SortableHeader title="Vol (24h)" columnId="volume24h" sorting={sorting} onSort={onSort} />
    ),
    cell: ({ getValue }) => formatCurrencyValue(getValue(), formatFiat),
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: () => (
      <SortableHeader title="Age" columnId="createdAt" sorting={sorting} onSort={onSort} />
    ),
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return '-';

      return formatTimeAgo(value);
    },
  }),
  columnHelper.accessor('uniqueHolders', {
    id: 'uniqueHolders',
    header: () => (
      <SortableHeader title="Holders" columnId="uniqueHolders" sorting={sorting} onSort={onSort} />
    ),
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return '-';

      return shortFormatter.format(value);
    },
  }),
];

// Constants
const INITIAL_VISIBLE_ROWS = 4;
const MOBILE_COLUMN_WIDTHS = ['40%', '30%', '30%'];
const DESKTOP_COLUMN_WIDTHS = ['28%', '20%', '18%', '18%', '16%'];
const MOBILE_HIDDEN_COLUMNS = ['createdAt', 'uniqueHolders'];

// Helper functions
const isZoraDataEmpty = (zora: ZoraToken | undefined): boolean => {
  return Boolean(
    zora &&
      zora.coin === null &&
      zora.profile === null &&
      zora.profileCoins === null &&
      zora.type === null,
  );
};

const getCreatorDisplayName = (profile?: ZoraToken['profile']): string => {
  if (!profile) return '';
  return profile.displayName || profile.username || profile.handle || '';
};

const formatCurrencyValue = (
  value: string | undefined,
  formatFiat: (
    amount: number | string,
    showExtraSmallAmounts?: boolean,
    showDecimals?: boolean,
  ) => string,
): string => {
  if (!value) return '-';
  const numericValue = parseFloat(value);
  return Number.isNaN(numericValue) ? '-' : formatFiat(numericValue);
};

interface Props {
  zora: ZoraToken | undefined;
}

export const ZoraProfileCoins = ({ zora }: Props) => {
  const { formatFiat } = useFiatDisplay();
  const [isExpanded, setIsExpanded] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'marketCap', desc: true }]);
  const isMobile = useMedia(breakpoints.mobile, false);
  const router = useRouter();

  const allData = useMemo(() => {
    if (!zora?.profileCoins?.edges) return [];

    return zora.profileCoins.edges
      .filter((edge) => edge.node)
      .map((edge) => edge.node as ZoraTokenNode);
  }, [zora?.profileCoins]);

  const handleSort = (columnId: string, direction: 'asc' | 'desc') => {
    setSorting([{ id: columnId, desc: direction === 'desc' }]);
  };

  const columns = useMemo(() => {
    return createColumns(sorting, handleSort, formatFiat);
  }, [sorting, formatFiat]);

  const visibleColumns = useMemo(() => {
    if (isMobile) {
      // Filter out Age and Holders columns on mobile
      return columns.filter((col) => !MOBILE_HIDDEN_COLUMNS.includes(col.id as string));
    }
    return columns;
  }, [columns, isMobile]);

  const table = useReactTable({
    data: allData,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const sortedRows = table.getSortedRowModel().rows;
  const displayData = useMemo(() => {
    if (isExpanded || sortedRows.length <= INITIAL_VISIBLE_ROWS) {
      return sortedRows;
    }
    return sortedRows.slice(0, INITIAL_VISIBLE_ROWS);
  }, [sortedRows, isExpanded]);

  const handleRowClick = (token: ZoraTokenNode) => {
    if (token.address && token.chainId) {
      router.push({
        pathname: '/tokens/[chainId]/[...contractAddresses]',
        query: {
          chainId: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[token.chainId || CHAIN_IDS.BASE],
          contractAddresses: [token.address],
        },
      });
    }
  };

  // Return null if all Zora fields are null (no data available)
  if (isZoraDataEmpty(zora)) return null;

  // Show skeleton loader while zora data is loading
  if (!zora || !zora.type || !zora.profileCoins) {
    return (
      <div className="flex flex-col px-6 tablet:px-8 py-6 tablet:py-8 gap-8">
        <TextSkeleton height={32} width={200} />
        <TextSkeleton height={24} width="80%" />
        <TextSkeleton height={44} width="100%" lines={3} />
      </div>
    );
  }

  if (!zora?.profileCoins?.edges || zora.profileCoins.edges.length === 0) return null;

  const showMoreButton = allData.length > INITIAL_VISIBLE_ROWS;
  const creatorName = getCreatorDisplayName(zora.profile);
  const showTableFooter = isExpanded && zora?.profileCoins?.pageInfo?.hasNextPage;

  return (
    <div>
      <h2
        className={fontStyles({
          variant: 'H5',
          class: ['flex', 'px-6', 'tablet:px-8', 'pt-6', 'tablet:pt-8', 'pb-6'],
        })}
      >
        Created by {creatorName || 'creator'}
      </h2>

      <div
        className={fontStyles({
          variant: 'BODY_LG',
          class: ['text-token-gray-800', 'px-6', 'tablet:px-8', 'pb-8'],
        })}
      >
        The following post coins were created by{' '}
        {zora.profile?.creatorCoin?.address ? (
          <Link
            href={{
              pathname: '/tokens/[chainId]/[...contractAddresses]',
              query: {
                chainId: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[CHAIN_IDS.BASE],
                contractAddresses: [zora.profile.creatorCoin.address],
              },
            }}
            className="inline-flex items-center gap-1 text-token-gray-1000 group align-middle"
            scroll={true}
          >
            <Avatar
              src={makeImageProxyUrl(CHAIN_IDS.BASE, zora.profile.creatorCoin.address)}
              alt={creatorName || 'creator'}
              size={20}
            />
            <span className="font-medium group-hover:underline">
              {creatorName || 'this creator'}
            </span>
          </Link>
        ) : (
          <span className="font-medium">{creatorName || 'this creator'}</span>
        )}
      </div>

      <div
        className={classNames(
          'overflow-hidden',
          !showMoreButton && !showTableFooter ? 'rounded-b-[26px]' : undefined,
        )}
      >
        <table className="w-full table-fixed">
          <colgroup>
            {(isMobile ? MOBILE_COLUMN_WIDTHS : DESKTOP_COLUMN_WIDTHS).map((width, index) => (
              <col key={index} className={`w-[${width}]`} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-token-gray-200">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="font-body font-medium text-[14px] leading-[16px] text-left text-token-gray-800 px-6 tablet:px-8 pb-3 whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {displayData.map((row) => (
              <tr
                key={row.id}
                className="border-b border-token-gray-200 hover:bg-token-alphalight-100 last:border-b-0 transition-colors cursor-pointer"
                onClick={() => handleRowClick(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={classNames(
                      fontStyles({ variant: 'BODY_MD' }),
                      'px-6 tablet:px-8 py-3 align-middle',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {showTableFooter && (
            <tfoot>
              <tr>
                <td colSpan={visibleColumns.length} className="p-0">
                  <div
                    className={classNames(
                      'border-t border-token-gray-200',
                      !showMoreButton ? 'rounded-b-[26px] overflow-hidden' : undefined,
                    )}
                  >
                    <ExternalLink
                      href={`https://zora.co/@${zora.profile?.handle || ''}`}
                      onClick={() => {
                        ampli.zoraProfileCoinsClicked({
                          creator_contract_address: zora.profile?.creatorCoin?.address,
                        });
                      }}
                      className={fontStyles({
                        variant: 'LABEL_MD',
                        class: [
                          'flex',
                          'items-center',
                          'justify-center',
                          'gap-2',
                          'w-full',
                          'h-[44px]',
                          'text-token-purple-900',
                          'hover:bg-token-alphalight-100',
                          'hover:underline',
                          'transition-colors',
                          'duration-200',
                        ],
                      })}
                    >
                      {creatorName ? (
                        <>
                          View more coins by{' '}
                          {zora.profile?.creatorCoin?.address && (
                            <Avatar
                              src={makeImageProxyUrl(
                                CHAIN_IDS.BASE,
                                zora.profile.creatorCoin.address,
                              )}
                              alt={creatorName}
                              size={24}
                            />
                          )}{' '}
                          {creatorName} on Zora
                        </>
                      ) : (
                        'View more coins on Zora'
                      )}
                      <ArrowExternalIcon className="w-4 h-4" />
                    </ExternalLink>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showMoreButton && (
        <button
          className={fontStyles({
            variant: 'LABEL_MD',
            class:
              'cursor-pointer text-center w-full flex justify-center items-center h-[44px] border-t border-token-gray-200 text-token-gray-800 transition-colors duration-300 ease-in-out rounded-b-[26px] hover:bg-token-alphalight-100',
          })}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : 'Show more'}{' '}
          <span
            data-state={isExpanded ? 'open' : 'closed'}
            className="text-token-gray-800 ml-1 transition-transform duration-300 data-[state=open]:rotate-x-180 data-[state=closed]:rotate-x-0"
          >
            <ChevronDownSmallIcon />
          </span>
        </button>
      )}
    </div>
  );
};
